import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { api, errMsg, formatDate, mediaUrl } from "@/lib/api";
import { EntityManager } from "./EntityManager";

const fields = [
  { name: "title", label: "Titre de l'épisode", required: true, full: true },
  { name: "host", label: "Animateur / auteur" },
  { name: "duration", label: "Durée (ex. 32:15)" },
  { name: "description", label: "Description", type: "textarea", rows: 3, full: true },
  { name: "audio_url", label: "Fichier audio", type: "file", accept: "audio/*", placeholder: "Téléverser un audio (MP3, WAV…)", full: true },
  { name: "cover_url", label: "Visuel", type: "file", accept: "image/*", placeholder: "Téléverser une image", full: true },
  { name: "published", label: "Publié", type: "switch" },
];
const columns = [
  { key: "title", label: "Épisode", render: (p) => <div><div className="font-semibold line-clamp-1">{p.title}</div><div className="text-xs text-zinc-500">{p.host} · {p.duration}</div></div> },
  { key: "created_at", label: "Date", render: (p) => formatDate(p.created_at) },
  { key: "published", label: "Statut", render: (p) => <span className={`text-xs font-bold ${p.published ? "text-green-600" : "text-zinc-400"}`}>{p.published ? "Publié" : "Brouillon"}</span> },
];

const Submissions = ({ onPublished }) => {
  const [subs, setSubs] = useState([]);
  const load = useCallback(() => api.get("/admin/podcast-submissions").then((r) => setSubs(r.data.filter((s) => s.status === "pending"))).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, approve) => {
    try {
      if (approve) await api.post(`/admin/podcast-submissions/${id}/approve`); else await api.delete(`/admin/podcast-submissions/${id}`);
      toast.success(approve ? "Audio publié comme épisode" : "Dépôt rejeté");
      load();
      if (approve) onPublished();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-10" data-testid="podcast-submissions">
      <h2 className="font-heading text-xl font-black border-l-4 border-red-600 pl-3 mb-4">Audios déposés par le public <span className="text-zinc-400 text-sm font-normal">({subs.length} en attente)</span></h2>
      {!subs.length && <p className="text-sm text-zinc-500 bg-white border p-4">Aucun dépôt en attente.</p>}
      <div className="grid gap-3">
        {subs.map((s, i) => (
          <div key={s.id} data-testid={`submission-${i}`} className="bg-white border border-zinc-200 p-4 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 min-w-0"><div className="font-semibold">{s.title}</div><div className="text-xs text-zinc-500">{s.name} {s.email && `· ${s.email}`} · {formatDate(s.created_at)}</div><p className="text-sm text-zinc-600 mt-1">{s.description}</p><audio controls src={mediaUrl(s.audio_url)} className="mt-2 h-9 w-full max-w-md" /></div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`submission-approve-${i}`} onClick={() => act(s.id, true)} className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-2 text-xs font-bold uppercase hover:bg-green-700"><Check size={14} /> Publier</button>
              <button data-testid={`submission-reject-${i}`} onClick={() => act(s.id, false)} className="inline-flex items-center gap-1 border border-zinc-300 px-3 py-2 text-xs font-bold uppercase hover:bg-red-50 hover:text-red-600"><X size={14} /> Rejeter</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DEFAULTS = { title: "", host: "Chronique.cd", duration: "", description: "", audio_url: "", cover_url: "", published: true };
const renderExtra = (reload) => <Submissions onPublished={reload} />;

export default function PodcastsManager() {
  return (
    <EntityManager title="Podcasts" description="Publiez vos épisodes audio et validez les dépôts du public." listUrl="/admin/podcasts" baseUrl="/podcasts" fields={fields} columns={columns} testId="podcasts" defaults={DEFAULTS} renderExtra={renderExtra} />
  );
}
